import React, { useEffect } from 'react'

import { ButtonClean, Checkbox, Fieldset } from '@fourthwall/components'
import { routing } from '@utils/routing'

import { isPaidTier } from '../../../../utils'
import { useTierContext } from '../../hooks/useTierContext'
import { FormFields } from '../../providers/TierProvider/types'
import { fillCurrencyInputsWithFetchedPriceRates } from '../../providers/TierProvider/utils'
import { CurrenciesTable } from '../CurrenciesTable'
import { DefaultExchangeRate } from '../DefaultExchangeRate'
import * as S from './styled'

export const InternationalPricing = () => {
  const { form, tier, isLoading, updatedCurrencyConversion, priceRates } = useTierContext()

  // Fill all currency inputs on "Automatically convert international pricing" check
  useEffect(() => {
    if (!form.values.convertInternationalPricing) return
    if (tier && !('pricesAutomaticallyConverted' in tier)) return

    // with initial values if editing tier, auto convert is initial value and user did not clicked "Update currency rates"
    if (
      tier &&
      isPaidTier(tier) &&
      form.initialValues.convertInternationalPricing === true &&
      !updatedCurrencyConversion &&
      form.values.price === form.initialValues.price
    ) {
      return Object.keys(tier.variants.monthly.amounts).forEach((key) => {
        form.setFieldValue(
          `${[FormFields.currencies]}.${key}`,
          form.initialValues.currencies[key as keyof typeof tier.variants.monthly.amounts],
        )
      })
    }

    // with empty strings if there are no fetched auto rates (error)
    if (!priceRates) {
      return Object.keys(form.initialValues.currencies).forEach((key) => {
        form.setFieldValue(`${[FormFields.currencies]}.${key}`, '')
      })
    }

    // with fetched price rates
    fillCurrencyInputsWithFetchedPriceRates(form, priceRates)
  }, [form.values.convertInternationalPricing])

  const getDisabled = () => {
    if (isLoading) return true

    if (parseFloat(form.values.price) === 0) return true

    if (
      tier &&
      form.initialValues.convertInternationalPricing === false &&
      tier.statistics.membersCount > 0
    )
      return true

    return false
  }

  if (tier && !isPaidTier(tier)) return null

  return (
    <>
      <Fieldset.Row>
        <Fieldset.Item>
          <Checkbox
            label={
              <>
                Automatically convert tier price to all currencies (
                <ButtonClean
                  label="learn more"
                  href="https://help.fourthwall.com/hc/en-us/articles/18300828600347-Multi-Currency-Settings"
                  target="_blank"
                  withUnderline
                />
                )
              </>
            }
            disabled={getDisabled()}
            tooltip={
              <>
                <p>
                  We’ll convert your price to international currencies so that you earn about the
                  same amount of profit (in USD) from international members. Once calculated, your
                  membership price will stay fixed, so your earnings in USD will vary slightly
                  month-to-month due to exchange rate fluctuations.
                </p>
                {tier &&
                  form.initialValues.convertInternationalPricing === false &&
                  tier.statistics.membersCount > 0 && (
                    <p>
                      <br />
                      Automatic currency conversion update is not available when there are active
                      members.
                    </p>
                  )}
              </>
            }
            {...form.getFieldProps(FormFields.convertInternationalPricing)}
          />
        </Fieldset.Item>
      </Fieldset.Row>
      <DefaultExchangeRate />
      {!form.values.convertInternationalPricing && (
        <>
          <CurrenciesTable />
          <Fieldset.Row inner>
            <S.CurrenciesManageLinkContainer>
              <ButtonClean
                appearance="secondary"
                label="Manage international currencies"
                to={routing.settings.checkout}
              />
            </S.CurrenciesManageLinkContainer>
          </Fieldset.Row>
        </>
      )}
    </>
  )
}
